<template>
  <hr v-if="props.isShowHr" class="border-gray-500" />

  <nav
    :class="`flex justify-center  overflow-x-hidden  mt-${props.isShowHr ? '4' : '7'}`"
    aria-label="Pagination"
  >
    <div class="w-full p-1 px-4">
      <div class="relative w-full overflow-x-hidden">
        <div class="rich-content" v-html="renderedContent" />
        <div v-if="isLongText" class="flex justify-center mt-4">
          <button
            class="flex items-center text-[#ef3d23] gap-1 text-sm font-semibold hover:underline sm:text-base"
            @click="toggleExpand"
          >
            <span>{{ expanded ? btnSeeLess : btnSeeMore }}</span>
            <svg
              v-if="!expanded"
              xmlns="http://www.w3.org/2000/svg"
              class="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 9l-7 7-7-7"
              />
            </svg>
            <svg
              v-else
              xmlns="http://www.w3.org/2000/svg"
              class="w-4 h-4 transform rotate-180"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  </nav>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  description: {
    type: Object as PropType<{
      contentDescriptionEn: string | null
      contentDescriptionTh: string | null
    }>,
    required: true
  },
  isShowHr: {
    type: Boolean,
    default: false
  },
  maginTop: { type: String, default: '' }
})

const maxVisibleChars = 400
const renderedContent = ref('')
const { locale, t } = useI18n()
const expanded = ref(false)
const toggleExpand = () => (expanded.value = !expanded.value)
const btnSeeMore = t('terms.seeMore')
const btnSeeLess = t('terms.SeeLess')
const displayContent = computed(() =>
  locale.value === 'th'
    ? props.description.contentDescriptionTh || ''
    : props.description.contentDescriptionEn || ''
)

const getVisibleCharCount = (html: string): number => {
  if (typeof window === 'undefined') return 0

  const div = document.createElement('div')
  div.innerHTML = html
  const plainText = div.textContent || ''
  return plainText.length
}

const wordCount = computed(() => getVisibleCharCount(displayContent.value))

const isLongText = computed(() => wordCount.value > maxVisibleChars)

const truncateHtmlByVisibleChars = (html: string, maxChars: number): string => {
  if (typeof window === 'undefined') return html

  const parser = new DOMParser()
  const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html')
  const container = doc.body
  if (!container) return html

  let visibleCharCount = 0
  let done = false

  const processNode = (node: Node): Node | null => {
    if (done) return null

    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || ''
      const chars: string[] = []
      for (let i = 0; i < text.length; i++) {
        if (visibleCharCount >= maxChars) {
          done = true
          break
        }
        chars.push(text[i])
        visibleCharCount++
      }

      return document.createTextNode(
        done ? chars.join('').trimEnd() + '...' : text
      )
    }

    if (node.nodeType === Node.ELEMENT_NODE) {
      const clone = node.cloneNode(false)
      for (const child of Array.from(node.childNodes)) {
        const childClone = processNode(child)
        if (childClone) clone.appendChild(childClone)
        if (done) break
      }
      return clone
    }

    return null
  }

  const wrapper = document.createElement('div')
  for (const child of Array.from(container.childNodes)) {
    const processed = processNode(child)
    if (processed) wrapper.appendChild(processed)
    if (done) break
  }

  return wrapper.innerHTML
}

const updateRenderedContent = () => {
  renderedContent.value =
    expanded.value || !isLongText.value
      ? displayContent.value
      : truncateHtmlByVisibleChars(displayContent.value, maxVisibleChars)
}

onMounted(() => {
  updateRenderedContent()
})

watch([expanded, displayContent, isLongText], updateRenderedContent)
</script>

<style>
.rich-content {
  overflow-wrap: break-word;
  word-break: break-word;
  word-wrap: break-word;
  white-space: normal;
  max-width: 100%;
}

.rich-content a {
  word-break: break-all;
  overflow-wrap: anywhere;
  display: inline-block;
  max-width: 100%;
}

.rich-content h2 {
  font-size: 20px;
  font-weight: bold;
  line-height: 1.3;
}
.rich-content h3 {
  font-size: 18px;
  font-weight: 600;
  line-height: 1.4;
}
.rich-content h4 {
  font-size: 16px;
  font-weight: 600;
  line-height: 1.4;
}
.rich-content h5 {
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
}
.rich-content h6 {
  font-size: 10px;
  font-weight: 600;
  line-height: 1.4;
}
.rich-content p {
  font-size: 16px;
  line-height: 1.75;
}
.rich-content a {
  color: #2563eb;
  text-decoration: underline;
}
.rich-content a:hover {
  color: #1d4ed8;
}
.rich-content h1 {
  font-weight: bold;
  line-height: 1.25;
  font-size: 36px;
}

@media screen and (min-width: 769px) {
  .rich-content h1 {
    font-size: 48px;
  }
}

.rich-content li[data-list='ordered'] {
  list-style-type: decimal;
  margin-left: 1.5em;
}
.rich-content li[data-list='bullet'] {
  list-style-type: disc;
  margin-left: 1.5em;
}

.rich-content .ql-align-center {
  text-align: center;
}
.rich-content .ql-align-right {
  text-align: right;
}

.rich-content li[data-list='ordered'] {
  list-style-type: decimal;
  margin-left: 1.5em;
}
.rich-content li[data-list='bullet'] {
  list-style-type: disc;
  margin-left: 1.5em;
}

.rich-content a {
  color: #2563eb;
  text-decoration: underline;
}
.rich-content a:hover {
  color: #1d4ed8;
}
.ql-editor .ql-font-cabinetGrotesk-variable,
.rich-content .ql-font-cabinetGrotesk-variable {
  font-family: 'CabinetGrotesk-Variable_case_club', sans-serif;
}
.ql-editor .ql-font-default,
.rich-content .ql-font-default {
  font-family: 'CabinetGrotesk-Variable_case_club', sans-serif;
}
.ql-editor .ql-font-anuphan,
.rich-content .ql-font-anuphan {
  font-family: 'Anuphan', sans-serif;
}

.ql-editor .ql-font-anuphan {
  font-family: 'Anuphan', sans-serif;
}
.rich-content .ql-editor .ql-font-cabinetGrotesk-variable {
  font-family: 'CabinetGrotesk-Variable_case_club', sans-serif;
}
.ql-editor .ql-font-cabinetGrotesk-variable {
  font-family: 'CabinetGrotesk-Variable_case_club', sans-serif;
}
.ql-editor .ql-font-default {
  font-family: 'CabinetGrotesk-Variable_case_club', sans-serif;
}
.rich-content p:has(> br:only-child) {
  margin-bottom: 0 !important;
}
</style>
